// components/ui/data-table.tsx
'use client';

import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
} from '@rocket-house-productions/shadcn-ui';
import {
  ColumnDef,
  flexRender,
  tableFeatures,
  stockFeatures,
  StockFeatures,
  createFilteredRowModel,
  createSortedRowModel,
  createPaginatedRowModel,
  SortingState,
  useTable,
  RowData,
} from '@tanstack/react-table';

const _features = tableFeatures({
  ...stockFeatures,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
});
import { Button } from '@rocket-house-productions/shadcn-ui/server';

type DataTableProps<TData extends RowData> = {
  columns: ColumnDef<StockFeatures, TData>[];
  data: TData[];
  searchColumnId?: string; // optional column id to bind to the search box
  searchPlaceholder?: string;
  pageSize?: number;
};

export function DataTable<TData extends RowData>({
  columns,
  data,
  searchColumnId,
  searchPlaceholder = 'Search…',
  pageSize = 10,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const table = useTable({
    features: _features,
    data,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters as any,
    initialState: { pagination: { pageSize: pageSize, pageIndex: 0 } },
  });

  const searchCol = searchColumnId ? table.getColumn(searchColumnId) : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        {searchCol ? (
          <Input
            placeholder={searchPlaceholder}
            value={(searchCol.getFilterValue() as string) ?? ''}
            onChange={e => searchCol.setFilterValue(e.target.value)}
            className="w-64"
          />
        ) : (
          <div />
        )}
        <div className="text-muted-foreground text-sm">{table.getFilteredRowModel().rows.length} results</div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(hg => (
              <TableRow key={hg.id}>
                {hg.headers.map(header => (
                  <TableHead
                    key={header.id}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    className={header.column.getCanSort() ? 'cursor-pointer select-none' : undefined}>
                    <span className="inline-flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() ? (
                        <span className="text-muted-foreground text-[10px]">
                          {{ asc: '▲', desc: '▼' }[header.column.getIsSorted() as string] ?? '↕'}
                        </span>
                      ) : null}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-muted-foreground h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-sm">
          Page {table.state.pagination.pageIndex + 1} of {table.getPageCount() || 1}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}>
            Prev
          </Button>
          <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
